import { z } from 'zod'
import { GENERO, USUARIO_ESTADO_CON_PASSWORD } from './enums.js'
import type { Genero, UsuarioEstado, UsuarioRol } from './enums.js'
import { codigoPais } from './paises.js'
import type { ProblemaValidacion } from './problema.js'

/**
 * Reglas de identidad: qué se puede cargar de una persona y qué implica su
 * email.
 *
 * No son reglas de juego, así que no van en `core`: son los mismos invariantes
 * que `usuario` declara como CHECK, puestos como funciones puras. Eso permite
 * mostrarlos en el formulario antes de escribir —en vez de traducir un error de
 * SQLite— y que la app y el servidor deriven exactamente el mismo estado de
 * cuenta a partir del email.
 */

/**
 * Lo que el organizador carga de una persona. Todo lo demás se deriva.
 *
 * `pais`, `genero` y `numero` viajan nullable y son obligatorios igual: el
 * `null` es el campo a medio completar mientras se escribe el formulario, no un
 * valor que se pueda guardar. `validarPersona` es quien no lo deja pasar.
 */
export interface DatosPersona {
  nombre: string
  apodo: string | null
  email: string | null
  /** Obligatorio. Código ISO 3166-1 alfa-2; ver [`PAISES`]. */
  pais: string | null
  /** Obligatorio. */
  genero: Genero | null
  /**
   * Obligatorio. El número global del jugador, el que se grita en el salón:
   * «el 47 no vino».
   *
   * `null` es el campo a medio completar mientras se escribe el formulario, no
   * un valor que se pueda guardar: una ficha sin número obliga a buscar a la
   * persona por el nombre justo cuando hay que apurar el check-in. El alta
   * llega con el que sigue ya escrito (lo propone [`proximoNumero`]), así que
   * el caso normal es no tocarlo; lo que no se puede es dejarlo en blanco.
   */
  numero: number | null
}

/** Cómo está hoy la cuenta que se va a editar. En un alta no hay nada de esto. */
export interface CuentaActual {
  estado: UsuarioEstado
  rol: UsuarioRol
}

/**
 * Con qué se ingresa, que no es lo mismo en los dos modos.
 *
 * `email` es la app de escritorio: la base es de la máquina, el email es único
 * en ella y es lo que la ata a una persona. `usuario` es la nube, donde el mismo
 * servidor atiende a varios clubes: ahí el email es un dato de contacto que se
 * puede repetir (una familia comparte casilla, un club anota a todos con la del
 * secretario) y la llave pasa a ser el nombre de usuario.
 *
 * Se declara acá y no se adivina porque decide qué campo es obligatorio para que
 * alguien pueda entrar, y equivocarse deja cuentas que no pueden ingresar o
 * fichas que no se pueden guardar.
 */
export type LlaveDeAcceso = 'email' | 'usuario'

export interface ContextoPersona {
  /**
   * Emails de las OTRAS personas del catálogo. Atajan el UNIQUE antes de
   * escribir; la comparación es sin distinguir mayúsculas, igual que el
   * COLLATE NOCASE de la columna.
   *
   * No se pasan cuando la llave es `usuario`: ahí el email se puede repetir.
   */
  emailsTomados?: Iterable<string>
  /** Números de las OTRAS personas. Ataja el UNIQUE de `usuario.numero`. */
  numerosTomados?: Iterable<number>
  /** Nulo en un alta. */
  cuenta?: CuentaActual | null
  /** Ver [`LlaveDeAcceso`]. Por omisión, la de la app de escritorio. */
  llave?: LlaveDeAcceso
}

const emailSchema = z.string().email()

/**
 * Deja los campos como se van a guardar: sin espacios de más, con los vacíos
 * en null y el email en minúsculas.
 *
 * Normalizar antes de validar es lo que hace que `"  "` y `""` sean el mismo
 * caso, y que dos personas no entren con el mismo email escrito distinto.
 */
export function normalizarPersona(datos: DatosPersona): DatosPersona {
  const email = limpiar(datos.email)
  const nombre = datos.nombre.trim()
  return {
    nombre,
    // El apodo dejó de pedirse en el formulario: nadie lo cargaba y una columna
    // vacía en el 95% de las fichas no distingue a nadie. Se deriva del nombre
    // acá —y no en la pantalla— para que la app y el servidor guarden lo mismo
    // sobre la misma persona.
    apodo: limpiar(datos.apodo) ?? apodoDesdeNombre(nombre),
    email: email === null ? null : email.toLowerCase(),
    // Lo que se guarda es el código, venga como código o como nombre. Lo que no
    // se puede traducir se deja como vino —no se convierte en null— para que la
    // validación pueda decir cuál era: «Falta el país» sobre algo que sí se
    // había cargado manda a buscar el error donde no está.
    pais: codigoPais(datos.pais) ?? limpiar(datos.pais),
    // El select ofrece "Sin especificar" como cadena vacía, que es lo mismo que
    // no haberlo cargado: si entrara así, el CHECK de la base la rechazaría.
    genero: (limpiar(datos.genero) as Genero | null) ?? null,
    numero: limpiarNumero(datos.numero),
  }
}

function limpiar(valor: string | null | undefined): string | null {
  const texto = valor?.trim() ?? ''
  return texto.length === 0 ? null : texto
}

/**
 * El número tal como se va a guardar: un entero, o `null` si el campo quedó en
 * blanco: el formulario a medio completar, no una ficha sin número.
 *
 * Acepta texto porque del formulario sale texto, y devuelve `NaN` cuando lo
 * escrito no es un entero. Es el mismo criterio que el país: lo que no se puede
 * traducir NO se convierte en null, porque «falta el número» sobre algo que
 * alguien sí tipeó manda a buscar el error donde no está. Tanto el `null` como
 * ese `NaN` los nombra `validarPersona` y ninguno llega a escribirse.
 */
function limpiarNumero(valor: number | string | null | undefined): number | null {
  if (typeof valor === 'number') return Number.isInteger(valor) ? valor : Number.NaN

  const texto = valor?.trim() ?? ''
  if (texto.length === 0) return null

  const numero = Number(texto)
  return Number.isInteger(numero) ? numero : Number.NaN
}

/**
 * El apodo que le corresponde a un nombre: el de pila más la inicial del último
 * apellido — «Ana Bermúdez» queda en «Ana B.».
 *
 * Es el nombre corto con el que se llama a alguien en el salón, no un
 * identificador: no es único ni pretende serlo. Se queda con la inicial del
 * ÚLTIMO apellido y no del segundo nombre porque es lo que separa a los dos
 * Juan de una mesa, que es para lo que se lo mira.
 *
 * Devuelve `null` con un nombre vacío en vez de una cadena rara: ese caso lo
 * frena `validarPersona` con el nombre, y un apodo inventado sobre la nada
 * taparía el problema real.
 */
export function apodoDesdeNombre(nombre: string): string | null {
  const partes = nombre.trim().split(/\s+/).filter(Boolean)
  if (partes.length === 0) return null
  if (partes.length === 1) return partes[0]!

  const apellido = partes[partes.length - 1]!
  return `${partes[0]} ${apellido.slice(0, 1).toUpperCase()}.`
}

/**
 * Valida un alta o una edición del catálogo. Devuelve todos los problemas
 * juntos, no el primero: el formulario los muestra de una.
 *
 * Espera los datos ya normalizados.
 */
export function validarPersona(
  datos: DatosPersona,
  contexto: ContextoPersona = {},
): ProblemaValidacion[] {
  const problemas: ProblemaValidacion[] = []

  if (datos.nombre.length === 0) {
    problemas.push({ codigo: 'nombre_vacio', mensaje: 'La persona necesita un nombre.' })
  }

  // Los dos son parte de la ficha del jugador, no un extra: el país arma las
  // representaciones y el género las categorías, y completarlos "después" es lo
  // que deja un padrón a medias que no se puede agrupar por nada.
  if (datos.pais === null) {
    problemas.push({ codigo: 'pais_vacio', mensaje: 'Falta el país.' })
  }

  if (datos.genero === null) {
    problemas.push({ codigo: 'genero_vacio', mensaje: 'Falta el género.' })
  }

  // Salta con datos que no vinieron del selector —un import, una ficha de
  // cuando el campo era texto libre— y ahí es justo cuando hay que verlo.
  if (datos.pais !== null && codigoPais(datos.pais) === null) {
    problemas.push({
      codigo: 'pais_desconocido',
      mensaje: `«${datos.pais}» no es un país de la lista.`,
      detalle: { pais: datos.pais },
    })
  }

  // El formulario ofrece un select, así que esto solo salta con datos que no
  // vinieron de ahí —un import, una versión vieja—; y ahí es justo cuando hay
  // que verlo antes de que el CHECK de la base lo rechace sin explicación.
  if (datos.genero !== null && !GENERO.includes(datos.genero)) {
    problemas.push({
      codigo: 'genero_invalido',
      mensaje: `«${datos.genero}» no es un género de la lista.`,
      detalle: { genero: datos.genero },
    })
  }

  // Obligatorio como el país y el género: es la otra forma de nombrar a la
  // persona, y la que se usa en el salón. En blanco no significa «asignale el
  // que sigue» (esa es la sugerencia que el formulario ya trae escrita), así
  // que dejarlo vacío es una ficha a medio cargar.
  if (datos.numero === null) {
    problemas.push({ codigo: 'numero_vacio', mensaje: 'Falta el número del jugador.' })
  }

  // Lo que tampoco pasa es un número que no es un número, o uno que no sirve
  // para nombrar a nadie: el CHECK de la columna exige mayor que cero.
  if (datos.numero !== null && !Number.isInteger(datos.numero)) {
    problemas.push({
      codigo: 'numero_invalido',
      mensaje: 'El número del jugador tiene que ser un entero.',
    })
  } else if (datos.numero !== null && datos.numero <= 0) {
    problemas.push({
      codigo: 'numero_invalido',
      mensaje: 'El número del jugador tiene que ser mayor que cero.',
      detalle: { numero: datos.numero },
    })
  }

  if (datos.numero !== null && ocupado(datos.numero, contexto.numerosTomados)) {
    problemas.push({
      codigo: 'numero_duplicado',
      mensaje: `El número ${datos.numero} ya es de otra persona.`,
      detalle: { numero: datos.numero },
    })
  }

  if (datos.email !== null && !emailSchema.safeParse(datos.email).success) {
    problemas.push({
      codigo: 'email_invalido',
      mensaje: `«${datos.email}» no parece un email.`,
      detalle: { email: datos.email },
    })
  }

  // De acá para abajo, todo lo que el email implica por ser la llave: que no se
  // repita, y lo que su ausencia significa para la cuenta. En la nube no es la
  // llave (se repite y no abre ninguna puerta), así que esas reglas corren sobre
  // el nombre de usuario, en `validarNombreUsuario`. Lo de arriba sí vale en los
  // dos modos: un email mal escrito está mal escrito en cualquiera.
  if ((contexto.llave ?? 'email') === 'usuario') return problemas

  if (datos.email !== null && tomado(datos.email, contexto.emailsTomados)) {
    problemas.push({
      codigo: 'email_duplicado',
      mensaje: 'Ya hay otra persona con ese email.',
      detalle: { email: datos.email },
    })
  }

  // Quitarle el email a una cuenta que ya puede ingresar la dejaría con
  // contraseña y sin forma de usarla, que es justo lo que los CHECK de la base
  // no permiten.
  const cuenta = contexto.cuenta
  if (cuenta && datos.email === null) {
    if (USUARIO_ESTADO_CON_PASSWORD.includes(cuenta.estado)) {
      problemas.push({
        codigo: 'email_requerido_por_acceso',
        mensaje:
          'Esa persona ya definió su contraseña: no se le puede quitar el email sin darle de ' +
          'baja el acceso.',
        detalle: { estado: cuenta.estado },
      })
    }

    if (cuenta.rol !== 'user') {
      problemas.push({
        codigo: 'email_requerido_por_rol',
        mensaje: 'Nadie administra sin poder ingresar: un administrador necesita email.',
        detalle: { rol: cuenta.rol },
      })
    }
  }

  return problemas
}

function tomado(email: string, otros: Iterable<string> | undefined): boolean {
  if (!otros) return false
  for (const otro of otros) {
    if (otro.toLowerCase() === email) return true
  }
  return false
}

function ocupado(numero: number, otros: Iterable<number> | undefined): boolean {
  if (!otros) return false
  for (const otro of otros) {
    if (otro === numero) return true
  }
  return false
}

/**
 * El número que le toca al que entra: uno más que el mayor que haya cargado.
 *
 * No busca el hueco más chico a propósito. Un número que quedó libre en el medio
 * del padrón es el número de quien lo tenía: repartirlo de nuevo mezcla dos
 * historias en una, y en un club donde el padrón lleva años eso es una discusión
 * garantizada. Los huecos se dejan y la numeración avanza.
 */
export function proximoNumero(numeros: Iterable<number | null>): number {
  let maximo = 0
  for (const numero of numeros) {
    if (numero !== null && numero > maximo) maximo = numero
  }
  return maximo + 1
}

/**
 * Lo más largo que puede ser un nombre de usuario, y cómo se escribe.
 *
 * Minúsculas, dígitos, punto, guion y guion bajo, empezando por letra o número.
 * Es el mismo juego que acepta el CHECK de la columna en el servidor, y es
 * deliberadamente angosto: el nombre se dicta por teléfono el día que alguien no
 * puede entrar, así que no puede tener espacios, acentos ni mayúsculas que
 * después nadie sabe si escribió.
 */
export const NOMBRE_USUARIO_MAXIMO = 32
export const NOMBRE_USUARIO_PATRON = /^[a-z0-9][a-z0-9._-]*$/

/**
 * El nombre de usuario tal como se guarda: sin espacios de más y en minúsculas.
 *
 * En minúsculas y no como lo escribieron porque la columna es
 * case-insensitive: `Ana` y `ana` son el mismo usuario, y guardar la forma
 * tipeada haría que la lista lo muestre distinto según quién lo cargó.
 */
export function normalizarNombreUsuario(valor: string | null | undefined): string | null {
  const texto = valor?.trim() ?? ''
  return texto.length === 0 ? null : texto.toLowerCase()
}

/**
 * Un nombre de usuario propuesto a partir del nombre y el número de la persona.
 *
 * Es solo una sugerencia: el formulario la ofrece y alguien la acepta. No se
 * pone sola porque un usuario es dar acceso a la web, y la mayoría del padrón
 * juega sin entrar nunca.
 *
 * Nombre y primer apellido, sin acentos y en minúsculas, unidos por un punto, y
 * el número pegado al final: «Ana María Pérez Gómez», 12 → `ana.perez12`. Se
 * asume la forma «nombres apellido apellido»: con cuatro palabras o más el
 * apellido es la tercera, con dos o tres es la segunda. El número es lo que la
 * hace única dentro del club (es único ahí), y la que choca con otro club la
 * avisa el servidor al guardar.
 *
 * Devuelve null si del nombre no queda ninguna letra que usar.
 */
export function sugerirNombreUsuario(nombre: string, numero: number | null | undefined): string | null {
  const palabras = nombre
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .split(/\s+/)
    .map((palabra) => palabra.replace(/[^a-z0-9]/g, ''))
    .filter(Boolean)

  const primera = palabras[0]
  if (!primera) return null
  const apellido = palabras.length >= 4 ? palabras[2] : palabras[1]

  const sufijo = numero && numero > 0 ? String(numero) : ''
  const base = (apellido ? `${primera}.${apellido}` : primera).slice(0, NOMBRE_USUARIO_MAXIMO - sufijo.length)
  const sugerido = `${base.replace(/\.$/, '')}${sufijo}`
  return NOMBRE_USUARIO_PATRON.test(sugerido) ? sugerido : null
}

/**
 * Las reglas del nombre de usuario, que en la nube es la llave de acceso.
 *
 * Es el gemelo de las tres últimas comprobaciones de [`validarPersona`], sobre
 * la columna que en ese modo abre la puerta: quién puede entrar, quién no puede
 * dejar de poder, y que no haya dos personas con el mismo nombre.
 *
 * Va aparte y no adentro de `validarPersona` porque el nombre de usuario no es
 * un dato de la ficha del jugador: es de la cuenta. Una instalación de
 * escritorio no lo tiene y no lo pide.
 */
export function validarNombreUsuario(
  usuario: string | null,
  contexto: { cuenta?: CuentaActual | null; usuariosTomados?: Iterable<string> } = {},
): ProblemaValidacion[] {
  const problemas: ProblemaValidacion[] = []

  if (usuario !== null) {
    if (usuario.length > NOMBRE_USUARIO_MAXIMO) {
      problemas.push({
        codigo: 'usuario_largo',
        mensaje: `El nombre de usuario no puede pasar de ${NOMBRE_USUARIO_MAXIMO} caracteres.`,
      })
    } else if (!NOMBRE_USUARIO_PATRON.test(usuario)) {
      problemas.push({
        codigo: 'usuario_invalido',
        mensaje:
          'El nombre de usuario va en minúsculas y sin espacios: letras, números, punto, ' +
          'guion o guion bajo.',
        detalle: { usuario },
      })
    }

    if (tomado(usuario, contexto.usuariosTomados)) {
      problemas.push({
        codigo: 'usuario_duplicado',
        mensaje: `El nombre de usuario «${usuario}» ya es de otra persona.`,
        detalle: { usuario },
      })
    }
  }

  // Las mismas dos reglas que el email tiene en la app de escritorio, sobre la
  // llave que corresponde acá: una cuenta con contraseña que se queda sin
  // usuario no tendría con qué entrar, y nadie administra sin poder entrar.
  const cuenta = contexto.cuenta
  if (cuenta && usuario === null) {
    if (USUARIO_ESTADO_CON_PASSWORD.includes(cuenta.estado)) {
      problemas.push({
        codigo: 'usuario_requerido_por_acceso',
        mensaje:
          'Esa persona ya definió su contraseña: no se le puede quitar el nombre de usuario ' +
          'sin darle de baja el acceso.',
        detalle: { estado: cuenta.estado },
      })
    }

    if (cuenta.rol !== 'user') {
      problemas.push({
        codigo: 'usuario_requerido_por_rol',
        mensaje: 'Nadie administra sin poder ingresar: un administrador necesita usuario.',
        detalle: { rol: cuenta.rol },
      })
    }
  }

  return problemas
}

/**
 * El estado de cuenta que corresponde a la llave de acceso, que es el
 * invariante central de `usuario`: tener llave es exactamente poder entrar.
 *
 * La llave es el email en la app de escritorio y el nombre de usuario en la
 * nube (ver [`LlaveDeAcceso`]), y la cuenta se comporta igual con las dos: sin
 * llave la persona existe, juega y acumula historial, pero no puede ingresar. Al
 * ponerle una queda invitada, a la espera de que defina la contraseña por el
 * enlace. Si ya la definió, cambiarla no cambia nada: sigue activa (o
 * suspendida) como estaba.
 */
export function estadoDeCuenta(
  llave: string | null,
  estadoPrevio: UsuarioEstado | null = null,
): UsuarioEstado {
  if (llave === null) return 'sin_acceso'
  if (estadoPrevio === null || estadoPrevio === 'sin_acceso') return 'invitado'
  return estadoPrevio
}
